import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { playerAvatar } from "../lib/api";
import { toast } from "sonner";
import { Send, Ban, ShieldOff, Inbox as InboxIcon, ChevronLeft, Bell } from "lucide-react";
import { visibleInterval } from "../lib/visibleInterval";
import { useAuth } from "../lib/auth";

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function PartnerAvatar({ partner, size = 40 }) {
  const isSystem = partner?.system || partner?.user_id === "__system__";
  if (isSystem) {
    return (
      <div
        className="rounded-full bg-[#1C0D40] border-2 border-[#FFE600] flex items-center justify-center shrink-0"
        style={{ width: size, height: size, boxShadow: "0 0 10px #FFE60066" }}
      >
        <Bell size={size * 0.5} className="text-[#FFE600]" />
      </div>
    );
  }
  return (
    <img
      src={playerAvatar(partner.account_name, partner.player_avatar_updated_at)}
      alt={partner.account_name}
      className="rounded-full object-cover border-2 border-[#FF007F] shrink-0"
      style={{ width: size, height: size }}
    />
  );
}

function ConversationRow({ convo, active, onClick }) {
  const { partner, last, unread } = convo;
  return (
    <button
      data-testid={`inbox-convo-${partner.account_name}`}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all border ${
        active
          ? "bg-[#FF007F]/15 border-[#FF007F]"
          : "border-transparent hover:bg-[#12082B] hover:border-[#39194D]"
      }`}
    >
      <PartnerAvatar partner={partner} size={44} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-heading uppercase text-sm truncate neon-cyan">
            {partner.account_name}
          </div>
          <div className="font-mono text-[10px] text-[#A89BCC] shrink-0">
            {formatTime(last?.created_at)}
          </div>
        </div>
        <div className="font-mono text-xs text-[#A89BCC] truncate">{last?.body}</div>
      </div>
      {unread > 0 && (
        <span
          data-testid={`inbox-convo-unread-${partner.account_name}`}
          className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-heading font-bold text-white shrink-0"
          style={{ background: "#FF007F", boxShadow: "0 0 8px #FF007F88" }}
        >
          {unread}
        </span>
      )}
    </button>
  );
}

function GiftClaim({ msg }) {
  const { refresh } = useAuth();
  const meta = msg.meta || {};
  const [claimed, setClaimed] = useState(!!meta.claimed);
  const [busy, setBusy] = useState(false);
  const claim = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/inbox/gift/${msg.msg_id}/claim`);
      setClaimed(true);
      toast.success(`+${Number(r.data.amount).toLocaleString()} Handshakes`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Claim failed");
      setClaimed(true);
    } finally { setBusy(false); }
  };
  return claimed
    ? <div data-testid={`gift-claimed-${msg.msg_id}`} className="mt-2 text-[#39FF14] font-heading uppercase tracking-widest">Claimed</div>
    : <button data-testid={`gift-claim-${msg.msg_id}`} disabled={busy} onClick={claim}
        className="mt-2 px-3 py-1 rounded-md bg-[#00F0FF]/20 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/30 font-heading uppercase tracking-widest disabled:opacity-50">
        Claim {Number(meta.amount || 0).toLocaleString()} {Number(meta.amount) === 1 ? "Handshake" : "Handshakes"}
      </button>;
}

function MessageBubble({ msg, isMine, isSystem, onAcceptTagTeam, onDeclineTagTeam }) {
  if (isSystem) {
    const meta = msg.meta || {};
    const isInvite = meta.kind === "tag_team_invite" && meta.team_id;
    return (
      <div className="flex justify-center my-2" data-testid="inbox-msg-system">
        <div
          className="px-4 py-2 rounded-lg text-xs font-mono text-center max-w-md border"
          style={{
            background: "#1C0D40",
            borderColor: "#FFE60066",
            color: "#FFE600",
            boxShadow: "0 0 8px #FFE60022",
          }}
        >
          <div>{msg.body}</div>
          {meta.kind === "handshake_gift" && <GiftClaim msg={msg} />}
          {isInvite && (
            <div className="flex gap-2 justify-center mt-2" data-testid={`tag-team-invite-${meta.team_id}`}>
              <button
                data-testid={`tt-accept-${meta.team_id}`}
                onClick={() => onAcceptTagTeam?.(meta.team_id)}
                className="px-2 py-1 rounded-md bg-[#39FF14]/20 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/30 font-heading uppercase tracking-widest"
              >Accept</button>
              <button
                data-testid={`tt-decline-${meta.team_id}`}
                onClick={() => onDeclineTagTeam?.(meta.team_id)}
                className="px-2 py-1 rounded-md bg-[#FF007F]/20 border border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F]/30 font-heading uppercase tracking-widest"
              >Decline</button>
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"} my-1`} data-testid={`inbox-msg-${msg.msg_id}`}>
      <div
        className="px-3 py-2 rounded-2xl max-w-[75%] font-mono text-sm break-words"
        style={{
          background: isMine ? "#FF007F" : "#12082B",
          color: isMine ? "#0A051A" : "#E5DCFF",
          border: isMine ? "none" : "1px solid #39194D",
        }}
      >
        <div>{msg.body}</div>
        <div className="text-[9px] mt-1 opacity-70 text-right">{formatTime(msg.created_at)}</div>
      </div>
    </div>
  );
}

function ThreadPane({ accountName, onBack, onAfterSend, onAfterBlockChange }) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/inbox/thread/${encodeURIComponent(accountName)}`);
      setThread(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load thread");
      setThread(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountName]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await api.post(`/inbox/thread/${encodeURIComponent(accountName)}`, { body });
      setText("");
      await load();
      onAfterSend && onAfterSend();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const toggleBlock = async () => {
    if (!thread?.partner || thread.partner.system) return;
    try {
      const url = thread.blocked
        ? `/inbox/unblock/${encodeURIComponent(thread.partner.account_name)}`
        : `/inbox/block/${encodeURIComponent(thread.partner.account_name)}`;
      await api.post(url);
      toast.success(thread.blocked ? "Player unblocked" : "Player blocked");
      await load();
      onAfterBlockChange && onAfterBlockChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="p-8 text-center font-heading neon-cyan">LOADING...</div>;
  if (!thread) return <div className="p-8 text-center font-heading neon-pink">CONVERSATION NOT FOUND</div>;

  const isSystem = thread.partner.system;
  return (
    <div className="flex flex-col h-full" data-testid="inbox-thread-pane">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#39194D] bg-[#12082B]/70">
        <button
          onClick={onBack}
          data-testid="inbox-thread-back"
          className="md:hidden p-1 text-[#A89BCC] hover:text-white"
          aria-label="Back to inbox"
        >
          <ChevronLeft size={20} />
        </button>
        <PartnerAvatar partner={thread.partner} size={40} />
        <div className="flex-1 min-w-0">
          <div className="font-heading uppercase text-lg neon-cyan truncate" data-testid="inbox-thread-partner-name">
            {thread.partner.account_name}
          </div>
          <div className="font-mono text-[10px] text-[#A89BCC] uppercase tracking-widest">
            {isSystem ? "System notifications" : "Direct message"}
          </div>
        </div>
        {!isSystem && (
          <button
            onClick={toggleBlock}
            data-testid="inbox-block-btn"
            className={`px-2 py-1 rounded-lg text-[10px] font-heading uppercase tracking-widest border transition-all flex items-center gap-1 ${
              thread.blocked
                ? "border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14]/10"
                : "border-[#FF007F]/60 text-[#FF007F] hover:bg-[#FF007F]/10"
            }`}
            title={thread.blocked ? "Unblock this player" : "Block this player"}
          >
            {thread.blocked ? <ShieldOff size={12} /> : <Ban size={12} />}
            {thread.blocked ? "Unblock" : "Block"}
          </button>
        )}
      </div>
      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3"
        style={{ minHeight: 0 }}
        data-testid="inbox-thread-messages"
      >
        {thread.messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-[#A89BCC] font-mono text-sm">
            No messages yet — say hi!
          </div>
        ) : (
          thread.messages.map((m) => (
            <MessageBubble
              key={m.msg_id}
              msg={m}
              isMine={m.from_user_id !== "__system__" && m.from_user_id !== thread.partner.user_id}
              isSystem={m.kind === "system" || m.from_user_id === "__system__"}
              onAcceptTagTeam={async (tid) => {
                try {
                  await api.post(`/tag-teams/accept/${tid}`);
                  toast.success("Tag Team formed!");
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}
              onDeclineTagTeam={async (tid) => {
                try {
                  await api.post(`/tag-teams/decline/${tid}`);
                  toast.success("Invite declined");
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}
            />
          ))
        )}
      </div>
      {/* Composer */}
      {!isSystem && (
        <div className="px-3 py-3 border-t border-[#39194D] bg-[#12082B]/70">
          {thread.blocked ? (
            <div className="text-center text-xs font-mono text-[#FF007F]">
              You have blocked this player. Unblock to resume messaging.
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                data-testid="inbox-send-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                placeholder="Type a message…"
                maxLength={500}
                className="flex-1 bg-[#0A051A] border border-[#39194D] rounded-lg px-3 py-2 text-sm font-mono text-white placeholder-[#695D8E] focus:outline-none focus:border-[#00F0FF]"
              />
              <button
                data-testid="inbox-send-btn"
                onClick={send}
                disabled={sending || !text.trim()}
                className="btn-neon px-4 py-2 flex items-center gap-2 disabled:opacity-50"
              >
                <Send size={14} />
                Send
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function InboxPage() {
  const { account_name: routeName } = useParams();
  const nav = useNavigate();
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inbox");
      setConvos(r.data.conversations || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load inbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return visibleInterval(load, 30000);
  }, []);

  const activeName = routeName;

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6" data-testid="inbox-page">
      <div className="flex items-center gap-2 mb-4">
        <InboxIcon size={22} className="text-[#00F0FF]" />
        <h1 className="font-heading text-3xl neon-cyan uppercase tracking-widest">Inbox</h1>
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-[320px_1fr] rounded-2xl border-2 border-[#FF007F]/40 overflow-hidden bg-[#0A051A]"
        style={{ height: "70vh", minHeight: 500, boxShadow: "0 0 24px #FF007F33" }}
      >
        {/* Conversations */}
        <div
          className={`border-r border-[#39194D] p-2 overflow-y-auto ${activeName ? "hidden md:block" : "block"}`}
          data-testid="inbox-convo-list"
        >
          {loading ? (
            <div className="p-4 text-center text-[#A89BCC] font-mono text-sm">Loading…</div>
          ) : convos.length === 0 ? (
            <div className="p-6 text-center text-[#A89BCC] font-mono text-sm">
              No conversations yet.
              <br />
              Open a player profile to send a message.
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {convos.map((c) => (
                <ConversationRow
                  key={c.partner.user_id}
                  convo={c}
                  active={
                    (activeName === c.partner.account_name) ||
                    (activeName === "System" && c.partner.system)
                  }
                  onClick={() => nav(`/inbox/${encodeURIComponent(c.partner.account_name)}`)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Thread */}
        <div className={`${activeName ? "block" : "hidden md:flex"} flex-col`} style={{ minHeight: 0 }}>
          {activeName ? (
            <ThreadPane
              accountName={activeName}
              onBack={() => nav("/inbox")}
              onAfterSend={load}
              onAfterBlockChange={load}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-[#A89BCC] font-mono text-sm">
              Select a conversation on the left.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
